import { MIN_MARGIN_AMOUNT, ZERO_WEI } from '@kwenta/sdk/constants'
import { PerpsProvider, PositionSide } from '@kwenta/sdk/types'
import { floorNumber, formatDollars } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { StyledBaseModal } from 'components/BaseModal'
import { TransactionButton } from 'components/Button/Button'
import ErrorView from 'components/ErrorView'
import { InfoBoxRow } from 'components/InfoBox'
import PreviewArrow from 'components/PreviewArrow'
import Spacer from 'components/Spacer'
import { setShowEditPositionModal } from 'state/app/reducer'
import { selectShowPositionModal, selectTransaction } from 'state/app/selectors'
import { clearTradeInputs, handleOneClickOperation } from 'state/futures/actions'
import { selectEditPositionInputs, selectUnlimitedApproval } from 'state/futures/common/selectors'
import {
	approveIsolatedMargin,
	editSmartMarginPositionMargin,
	submitIsolatedMarginAdjustMargin,
} from 'state/futures/isolatedMargin/actions'
import {
	selectEditMarginAllowanceValid,
	selectIsFetchingTradePreviewSmartMargin,
	selectIsolatedAvailableMargin,
} from 'state/futures/isolatedMargin/selectors'
import {
	selectDelegationTradingWarning,
	selectEditPositionModalInfo,
	selectEditPositionPreview,
	selectSubmittingFuturesTx,
} from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import UnderlineTabs from 'components/Button/UnderlineTabs'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import styled from 'styled-components'
import PositionType from '../PositionType'
import ApproveCheckbox from '../TradeConfirmation/ApproveCheckbox'
import EditPositionMarginInput from './EditPositionMarginInput'

export default function EditPositionMarginModal() {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()

	const transactionState = useAppSelector(selectTransaction)
	const isSubmitting = useAppSelector(selectSubmittingFuturesTx)
	const isFetchingPreview = useAppSelector(selectIsFetchingTradePreviewSmartMargin)
	const preview = useAppSelector(selectEditPositionPreview)
	const { marginDelta } = useAppSelector(selectEditPositionInputs)
	const idleMargin = useAppSelector(selectIsolatedAvailableMargin)
	const modal = useAppSelector(selectShowPositionModal)
	const { market, position: serializedPosition } = useAppSelector(selectEditPositionModalInfo)
	const allowanceValid = useAppSelector(selectEditMarginAllowanceValid)
	const showDelegationTradingWarning = useAppSelector(selectDelegationTradingWarning)
	const unlimitedApproval = useAppSelector(selectUnlimitedApproval)

	const positionDetails = useMemo(() => {
		if (!serializedPosition) return null
		return serializedPosition.details
	}, [serializedPosition])

	const [transferType, setTransferType] = useState(0)

	useEffect(() => {
		dispatch(clearTradeInputs())
	}, [dispatch])

	const isLoading = useMemo(
		() => isSubmitting || isFetchingPreview,
		[isSubmitting, isFetchingPreview]
	)

	const remainingMargin = useMemo(() => {
		if (!positionDetails) return null
		return wei(positionDetails.margin.remainingMargin)
	}, [positionDetails])

	const currentSize = useMemo(() => {
		return wei(positionDetails?.margin.notionalValue ?? 0)
	}, [positionDetails?.margin.notionalValue])

	const currentLeverage = useMemo(() => {
		return positionDetails?.margin.leverage ? wei(positionDetails.margin.leverage) : null
	}, [positionDetails?.margin.leverage])

	const maxWithdraw = useMemo(() => {
		const maxSize = remainingMargin?.mul(market?.appMaxLeverage ?? 1)
		const max = maxSize?.sub(currentSize).div(market?.appMaxLeverage ?? 1) ?? wei(0)
		const resultingMarginMax = remainingMargin?.sub(max) ?? wei(0)
		const remainingMarginMax = remainingMargin?.sub(MIN_MARGIN_AMOUNT) ?? wei(0)

		return max.lt(0) || remainingMarginMax.lt(0)
			? ZERO_WEI
			: resultingMarginMax.gte(MIN_MARGIN_AMOUNT)
				? max
				: remainingMarginMax
	}, [remainingMargin, currentSize, market?.appMaxLeverage])

	const maxUsdInputAmount = useMemo(
		() => (transferType === 0 ? wei(idleMargin) : maxWithdraw),
		[idleMargin, maxWithdraw, transferType]
	)

	const marginWei = useMemo(
		() => (!marginDelta || Number.isNaN(Number(marginDelta)) ? wei(0) : wei(marginDelta)),
		[marginDelta]
	)

	const invalid = useMemo(() => marginWei.gt(maxUsdInputAmount), [marginWei, maxUsdInputAmount])

	const maxLeverageExceeded = useMemo(
		() => transferType === 1 && currentLeverage?.gt(market?.appMaxLeverage ?? 1),
		[transferType, currentLeverage, market?.appMaxLeverage]
	)

	const previewError = useMemo(() => {
		if (maxLeverageExceeded) return 'Max leverage exceeded'
		if (preview?.showStatus) return preview?.statusMessage
		return null
	}, [preview?.showStatus, preview?.statusMessage, maxLeverageExceeded])

	const errorMessage = useMemo(
		() => previewError || transactionState?.error,
		[previewError, transactionState?.error]
	)

	const submitDisabled = useMemo(() => {
		return marginWei.eq(0) || invalid || isLoading || !!previewError || showDelegationTradingWarning
	}, [marginWei, invalid, isLoading, previewError, showDelegationTradingWarning])

	const onChangeTab = useCallback(
		(selection: number) => {
			setTransferType(selection)
			dispatch(editSmartMarginPositionMargin(''))
		},
		[dispatch]
	)

	const submitMarginChange = useCallback(() => {
		dispatch(submitIsolatedMarginAdjustMargin())
	}, [dispatch])

	const onClose = () => {
		if (modal?.marketAsset) {
			dispatch(editSmartMarginPositionMargin(''))
		}
		dispatch(setShowEditPositionModal(null))
	}

	const handleApproveSmartMargin = useCallback(async () => {
		dispatch(approveIsolatedMargin())
	}, [dispatch])

	const depositButtonText =
		allowanceValid || marginWei.eq(0)
			? t('futures.market.trade.edit-position.submit-margin-deposit')
			: t(
					`futures.market.trade.confirmation.modal.approve${
						unlimitedApproval ? '-unlimited' : ''
					}-spend`,
					{
						asset: preview?.provider === PerpsProvider.SNX_V2_OP ? 'sUSD' : 'USDC',
					}
				)

	const transferTypeTabs = useMemo(
		() => [
			{
				name: 'Deposit',
				label: t('futures.market.trade.edit-position.increase'),
				active: transferType === 0,
				onClick: (): void => {
					onChangeTab(0)
				},
			},
			{
				name: 'Withdraw',
				label: t('futures.market.trade.edit-position.decrease'),
				active: transferType === 1,
				onClick: (): void => {
					onChangeTab(1)
				},
			},
		],
		[transferType, onChangeTab, t]
	)

	const onClick = useCallback(() => {
		if (transferType === 0 && !allowanceValid) {
			handleApproveSmartMargin()
		} else {
			dispatch(handleOneClickOperation(submitMarginChange))
		}
	}, [dispatch, allowanceValid, transferType, submitMarginChange, handleApproveSmartMargin])

	return (
		<StyledBaseModal
			title={
				<FlexDivCol>
					<Body size="large">
						{transferType === 0 ? 'Increase Position Margin' : 'Reduce Position Margin'}
					</Body>
					<FlexDivRowCentered justifyContent="left">
						<Body weight="bold" color="secondary">
							{market?.marketName}
						</Body>
						<Spacer width={5} />
						<PositionType side={serializedPosition?.details.side ?? PositionSide.LONG} />
					</FlexDivRowCentered>
				</FlexDivCol>
			}
			isOpen
			onDismiss={onClose}
		>
			<Divider height={1} divider />

			<TabButtonsContainer>
				<UnderlineTabs options={transferTypeTabs} />
			</TabButtonsContainer>
			<Spacer height={20} />

			<EditPositionMarginInput
				maxUsdInput={maxUsdInputAmount.toString()}
				type={transferType === 0 ? 'deposit' : 'withdraw'}
			/>

			<Spacer height={20} />
			<InfoBoxRow
				textValueIcon={
					preview?.leverage && (
						<PreviewArrow showPreview>{floorNumber(preview.leverage, 2)}x</PreviewArrow>
					)
				}
				title={t('futures.market.trade.edit-position.leverage-change')}
				textValue={`${currentLeverage?.toString(2)}x`}
			/>
			<InfoBoxRow
				textValueIcon={
					preview?.leverage && (
						<PreviewArrow showPreview>
							{remainingMargin ? formatDollars(remainingMargin.add(marginWei)) : '-'}
						</PreviewArrow>
					)
				}
				title={t('futures.market.trade.edit-position.margin-change')}
				textValue={formatDollars(remainingMargin ?? 0)}
			/>
			<InfoBoxRow
				textValueIcon={
					preview?.leverage && (
						<PreviewArrow showPreview>
							{preview ? formatDollars(preview.liqPrice ?? 0) : '-'}
						</PreviewArrow>
					)
				}
				title={t('futures.market.trade.edit-position.liquidation')}
				textValue={formatDollars(positionDetails?.price.liquidationPrice || 0)}
			/>

			{/* Select trading mode */}
			{!allowanceValid && marginWei.gt(0) && <ApproveCheckbox provider={preview?.provider} />}
			<Spacer height={20} />

			<TransactionButton
				loading={isLoading}
				variant="flat"
				data-testid="futures-market-trade-deposit-margin-button"
				disabled={submitDisabled}
				fullWidth
				onClick={onClick}
			>
				{transferType === 0
					? depositButtonText
					: t('futures.market.trade.edit-position.submit-margin-withdraw')}
			</TransactionButton>

			{errorMessage && (
				<>
					<Spacer height={20} />
					<ErrorView message={errorMessage} formatter="revert" />
				</>
			)}
			{showDelegationTradingWarning && (
				<ErrorView
					messageType="error"
					message={t('futures.market.trade.confirmation.modal.disabled-delegation-trading')}
					containerStyle={{ margin: '16px 0 0 0' }}
				/>
			)}
		</StyledBaseModal>
	)
}

const TabButtonsContainer = styled.div`
	display: flex;
	min-height: 46px;
	gap: 15px;
	width: 100%;

	button {
		font-size: 13px;
	}

	> div > div {
		border-right: none;
		padding-left: 0px;
	}

	border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
`

const Divider = styled(Spacer)`
	width: 100%;
	margin-bottom: 10px;
`
